import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

import jdLogo from "../images/jd.svg";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const trapFocus = (event, container) => {
  const focusable = container.querySelectorAll(FOCUSABLE_SELECTOR);
  if (focusable.length === 0) {
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const OldSiteOverlay = () => {
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const dismiss = () => setVisible(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!visible) {
      setAnnouncement("");
      return undefined;
    }

    const announceTimer = window.setTimeout(() => {
      setAnnouncement(
        "Old website. Ján Ďuraš, Jan Duras, has moved to a new website: janduras.com."
      );
    }, 100);

    const root = document.getElementById("___gatsby");
    previouslyFocusedRef.current = document.activeElement;

    if (root) {
      root.setAttribute("inert", "");
    }

    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismiss();
        return;
      }

      if (event.key === "Tab" && dialog) {
        trapFocus(event, dialog);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(announceTimer);
      document.removeEventListener("keydown", handleKeyDown);

      if (root) {
        root.removeAttribute("inert");
      }

      previouslyFocusedRef.current?.focus?.();
    };
  }, [visible]);

  if (!visible || !mounted) {
    return null;
  }

  return createPortal(
    <div
      ref={dialogRef}
      className="old-site-notice old-site-overlay fixed inset-0 z-[9999] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="old-site-overlay-eyebrow old-site-overlay-title"
    >
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="old-site-overlay__sr-only"
      >
        {announcement}
      </div>
      <div
        className="old-site-overlay__backdrop absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative flex flex-1 items-center justify-center px-6">
        <div className="max-w-2xl text-center select-text">
          <img
            src={jdLogo}
            alt="Jan Duras JD logo"
            className="old-site-overlay__logo mx-auto mb-5 h-7 w-auto md:h-8"
            width="123"
            height="85"
          />
          <p
            id="old-site-overlay-eyebrow"
            className="old-site-overlay__chrome-text text-xs md:text-sm font-semibold uppercase tracking-[0.32em] mb-6"
          >
            OLD WEBSITE
          </p>
          <h2
            id="old-site-overlay-title"
            className="text-[var(--bone)] text-xl md:text-2xl font-normal leading-relaxed tracking-tight"
          >
            Ján Ďuraš (Jan Duras) has moved to a new website:
          </h2>
          <a
            href="https://janduras.com"
            className="old-site-overlay__link inline-flex items-center justify-center gap-3 md:gap-4 mt-4 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight"
            rel="noopener noreferrer"
          >
            janduras.com
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className="old-site-overlay__link-icon text-[0.55em]"
              aria-hidden="true"
            />
          </a>
        </div>
      </div>

      <div className="relative pb-10 flex justify-center">
        <button
          type="button"
          onClick={dismiss}
          className="old-site-overlay__dismiss text-sm md:text-base font-medium tracking-[0.12em] uppercase"
        >
          Browse site anyway
        </button>
      </div>
    </div>,
    document.body
  );
};

export default OldSiteOverlay;
